import type { TransactionWithRelations } from '../../../services/budgetApi';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Special filter value for uncategorized transactions only */
export const UNCATEGORIZED_FILTER = '__UNCATEGORIZED__';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SpendSeriesPoint {
  /** Day index within the year, starting at 0 (Jan 1). */
  dayIndex: number;
  /** Display label used by charts (e.g. "Jan 15"). */
  label: string;
  /** Cumulative spend for the current year at this point (null for future dates). */
  current: number | null;
  /** Cumulative spend for the previous year at this point. */
  previous: number;
  /** Cumulative spend for two years ago at this point. */
  twoYearsAgo: number;
}

export interface ComputeSpendSeriesOptions {
  /**
   * If set, include only this category.
   * - null/undefined: All expenses (includes uncategorized)
   * - UNCATEGORIZED_FILTER: Only uncategorized transactions
   * - category ID: Only that specific category
   */
  categoryId?: string | null;
  /** If set, include only this account. */
  accountId?: string | null;
  /** Override "today" for testing. */
  today?: Date;
  /** Category type to filter (default: 'expense'). Use 'income' for income categories. */
  categoryType?: 'expense' | 'income';
}

export interface SpendSummary {
  currentTotal: number;
  previousTotal: number;
  twoYearsAgoTotal: number;
  delta: number;
  percentChange: number | null;
}

export interface MonthlySpendPoint {
  /** Month index 0-11 (Jan=0, Dec=11) */
  monthIndex: number;
  /** Display label (e.g., "Jan", "Feb") */
  label: string;
  /** Dynamic properties for each category ID with spend amounts */
  [categoryId: string]: number | string;
}

export interface CategoryInfo {
  id: string;
  name: string;
  color: string;
}

export interface MonthlySpendData {
  year: number;
  points: MonthlySpendPoint[];
  categories: CategoryInfo[];
  totalByCategory: Map<string, number>;
}

export interface ComputeMonthlySpendOptions {
  year: number;
  categoryId?: string | null;
  accountId?: string | null;
  /** Category type to filter (default: 'expense'). Use 'income' for income categories. */
  categoryType?: 'expense' | 'income';
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/**
 * Parse a budget transaction date.
 *
 * The UI stores `Transaction.date` as a string and it may be:
 * - "YYYY-MM-DD" (date-only)
 * - full ISO timestamp (e.g. "2026-01-15T00:00:00.000Z")
 */
export function parseBudgetDate(dateStr: string): Date | null {
  if (!dateStr) return null;

  // Fast path for YYYY-MM-DD.
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})/.exec(dateStr);
  if (m) {
    const year = Number(m[1]);
    const monthIndex = Number(m[2]) - 1;
    const day = Number(m[3]);
    // Use UTC noon to avoid timezone/date boundary issues when converting.
    const d = new Date(Date.UTC(year, monthIndex, day, 12, 0, 0));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Returns a YYYY-MM-DD key in UTC (based on UTC date parts). */
export function toUTCDateKey(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function formatMonthDayUTC(d: Date): string {
  return `${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

export function startOfYearUTC(year: number): Date {
  return new Date(Date.UTC(year, 0, 1, 12, 0, 0));
}

export function addDaysUTC(d: Date, days: number): Date {
  return new Date(d.getTime() + days * 24 * 60 * 60 * 1000);
}

export function differenceInDaysUTC(a: Date, b: Date): number {
  // a - b in whole days
  const ms = a.getTime() - b.getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

/** Clamp a UTC day-of-year into the requested year, handling leap years. */
export function clampDayOfYearUTC(year: number, dayOfYearIndex: number): Date {
  // dayOfYearIndex: 0-based (Jan 1 = 0)
  const start = startOfYearUTC(year);
  return addDaysUTC(start, dayOfYearIndex);
}

/** Normalize a Date to UTC noon for consistent day-based calculations. */
export function toUTCNoon(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0));
}

/** Check if a year is a leap year. */
export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

/** Get the number of days in a year (365 or 366). */
export function daysInYear(year: number): number {
  return isLeapYear(year) ? 366 : 365;
}

// ---------------------------------------------------------------------------
// Core computation
// ---------------------------------------------------------------------------

function isExpenseTransaction(t: TransactionWithRelations): boolean {
  // Exclude transfer transactions from expense calculations
  if (t.category && t.category.type === 'transfer') return false;
  
  const amount = Number(t.amount);
  // Traditional check: negative amount means expense
  if (amount < 0) return true;
  // Category-based check: if amount is positive but category type is 'expense',
  // treat as expense (handles banks that export expenses as positive numbers)
  if (amount > 0 && t.category && t.category.type === 'expense') return true;
  return false;
}

function isIncomeTransaction(t: TransactionWithRelations): boolean {
  // Exclude transfer transactions from income calculations
  if (t.category && t.category.type === 'transfer') return false;
  
  const amount = Number(t.amount);
  // Traditional check: positive amount means income
  if (amount > 0 && (!t.category || t.category.type === 'income')) return true;
  // Category-based check: if category type is 'income', treat as income
  if (t.category && t.category.type === 'income') return true;
  return false;
}

function matchesCategory(t: TransactionWithRelations, categoryId: string | null | undefined): boolean {
  if (categoryId === undefined || categoryId === null) {
    // All expenses/income view includes any category, including uncategorized.
    return true;
  }
  if (categoryId === UNCATEGORIZED_FILTER) {
    // Only uncategorized transactions
    return t.categoryId === null;
  }
  return t.categoryId === categoryId;
}

function matchesAccount(t: TransactionWithRelations, accountId: string | null | undefined): boolean {
  if (!accountId) return true;
  return t.accountId === accountId;
}

function sumDailySpendByDateKey(
  transactions: TransactionWithRelations[],
  year: number,
  endDayOfYearIndex: number,
  categoryId: string | null | undefined,
  accountId: string | null | undefined,
  categoryType: 'expense' | 'income' = 'expense'
): Map<string, number> {
  const start = startOfYearUTC(year);
  const end = clampDayOfYearUTC(year, endDayOfYearIndex);

  // Choose the appropriate transaction type filter
  const transactionTypeFilter = categoryType === 'income' ? isIncomeTransaction : isExpenseTransaction;

  const byDay = new Map<string, number>();
  for (const t of transactions) {
    if (!transactionTypeFilter(t)) continue;
    if (!matchesCategory(t, categoryId)) continue;
    if (!matchesAccount(t, accountId)) continue;

    const parsed = parseBudgetDate(t.date);
    if (!parsed) continue;

    // Normalize to UTC date key.
    const key = toUTCDateKey(parsed);

    // Filter to year + range [start, end].
    // Use key compare only after computing date object in UTC.
    const dateUTC = new Date(`${key}T12:00:00.000Z`);
    if (dateUTC < start) continue;
    if (dateUTC > end) continue;
    if (dateUTC.getUTCFullYear() !== year) continue;

    const amount = Number(t.amount);
    byDay.set(key, (byDay.get(key) || 0) + amount);
  }

  return byDay;
}

export function computeCumulativeSpendSeries(
  transactions: TransactionWithRelations[],
  options: ComputeSpendSeriesOptions = {}
): {
  currentYear: number;
  previousYear: number;
  twoYearsAgoYear: number;
  points: SpendSeriesPoint[];
  summary: SpendSummary;
} {
  const rawToday = options.today ? new Date(options.today) : new Date();
  // Normalize to UTC noon to ensure consistent day-based calculations
  // regardless of time of day (avoids off-by-one errors early in the day)
  const today = toUTCNoon(rawToday);
  const currentYear = today.getUTCFullYear();
  const previousYear = currentYear - 1;
  const twoYearsAgoYear = currentYear - 2;

  const startCurrent = startOfYearUTC(currentYear);
  const currentDayIndex = Math.max(0, differenceInDaysUTC(today, startCurrent));

  const categoryType = options.categoryType || 'expense';

  // For current year: only get data up to today
  const currentByDay = sumDailySpendByDateKey(
    transactions,
    currentYear,
    currentDayIndex,
    options.categoryId,
    options.accountId,
    categoryType
  );
  
  // For previous years: get full year data
  const prevYearDays = daysInYear(previousYear) - 1;
  const twoYearsAgoDays = daysInYear(twoYearsAgoYear) - 1;
  
  const previousByDay = sumDailySpendByDateKey(
    transactions,
    previousYear,
    prevYearDays,
    options.categoryId,
    options.accountId,
    categoryType
  );
  const twoYearsAgoByDay = sumDailySpendByDateKey(
    transactions,
    twoYearsAgoYear,
    twoYearsAgoDays,
    options.categoryId,
    options.accountId,
    categoryType
  );

  // Generate points for full year
  const totalDaysInCurrentYear = daysInYear(currentYear) - 1;
  const points: SpendSeriesPoint[] = [];
  let currentCum = 0;
  let previousCum = 0;
  let twoYearsAgoCum = 0;
  
  // Track totals for summary (YTD only for comparison)
  let currentTotal = 0;
  let previousTotal = 0;
  let twoYearsAgoTotal = 0;
  
  for (let i = 0; i <= totalDaysInCurrentYear; i += 1) {
    const currentDate = clampDayOfYearUTC(currentYear, i);
    const prevDate = clampDayOfYearUTC(previousYear, i);
    const twoYearsAgoDate = clampDayOfYearUTC(twoYearsAgoYear, i);

    const currentKey = toUTCDateKey(currentDate);
    const prevKey = toUTCDateKey(prevDate);
    const twoYearsAgoKey = toUTCDateKey(twoYearsAgoDate);

    // For current year: only accumulate up to today, then use null
    if (i <= currentDayIndex) {
      currentCum += currentByDay.get(currentKey) || 0;
      currentTotal = currentCum;
    }
    
    // For previous years: accumulate full year
    previousCum += previousByDay.get(prevKey) || 0;
    twoYearsAgoCum += twoYearsAgoByDay.get(twoYearsAgoKey) || 0;
    
    // Track YTD totals for summary comparison
    if (i <= currentDayIndex) {
      previousTotal = previousCum;
      twoYearsAgoTotal = twoYearsAgoCum;
    }

    // Generate label at monthly intervals or first/last points
    let label = '';
    if (i === 0 || currentDate.getUTCDate() === 1 || i === totalDaysInCurrentYear) {
      label = formatMonthDayUTC(currentDate);
    }

    points.push({
      dayIndex: i,
      label,
      // Apply Math.abs() to the cumulative sums so expenses display above the x-axis
      // (expenses accumulate as negative values, but should show as positive spending)
      // This is applied to the sum (not individual transactions) to allow netting within categories
      current: i <= currentDayIndex ? Math.abs(currentCum) : null,
      previous: Math.abs(previousCum),
      twoYearsAgo: Math.abs(twoYearsAgoCum),
    });
  }

  // Apply absolute value to totals for consistent display
  const absCurrentTotal = Math.abs(currentTotal);
  const absPreviousTotal = Math.abs(previousTotal);
  const absTwoYearsAgoTotal = Math.abs(twoYearsAgoTotal);

  const delta = absCurrentTotal - absPreviousTotal;
  const percentChange = absPreviousTotal === 0 ? null : (delta / absPreviousTotal) * 100;

  return {
    currentYear,
    previousYear,
    twoYearsAgoYear,
    points,
    summary: {
      currentTotal: absCurrentTotal,
      previousTotal: absPreviousTotal,
      twoYearsAgoTotal: absTwoYearsAgoTotal,
      delta,
      percentChange
    },
  };
}

// ---------------------------------------------------------------------------
// Monthly spend by category
// ---------------------------------------------------------------------------

const DEFAULT_CATEGORY_COLORS = [
  '#3FB950', // Green
  '#4A90D9', // Blue
  '#9333EA', // Purple
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#EC4899', // Pink
  '#10B981', // Emerald
  '#F97316', // Orange
  '#6366F1', // Indigo
  '#14B8A6', // Teal
];

/**
 * Extract all unique years from transactions (sorted descending).
 */
export function getAvailableYears(transactions: TransactionWithRelations[]): number[] {
  const yearsSet = new Set<number>();
  for (const t of transactions) {
    const parsed = parseBudgetDate(t.date);
    if (parsed) {
      yearsSet.add(parsed.getUTCFullYear());
    }
  }
  return Array.from(yearsSet).sort((a, b) => b - a);
}

/**
 * Compute monthly spending totals by category for a specific year.
 */
export function computeMonthlySpendByCategory(
  transactions: TransactionWithRelations[],
  allCategories: CategoryInfo[],
  options: ComputeMonthlySpendOptions
): MonthlySpendData {
  const { year, categoryId, accountId, categoryType = 'expense' } = options;

  // Choose the appropriate transaction type filter
  const transactionTypeFilter = categoryType === 'income' ? isIncomeTransaction : isExpenseTransaction;

  // Filter transactions to the specified year
  const filtered = transactions.filter(t => {
    if (!transactionTypeFilter(t)) return false;
    if (!matchesCategory(t, categoryId)) return false;
    if (!matchesAccount(t, accountId)) return false;

    const parsed = parseBudgetDate(t.date);
    if (!parsed) return false;
    return parsed.getUTCFullYear() === year;
  });

  // Group transactions by month and category
  const monthlyData = new Map<number, Map<string, number>>();
  for (let month = 0; month < 12; month++) {
    monthlyData.set(month, new Map());
  }

  const categoriesSet = new Set<string>();
  for (const t of filtered) {
    const parsed = parseBudgetDate(t.date);
    if (!parsed) continue;

    const month = parsed.getUTCMonth(); // 0-11
    const catId = t.categoryId || 'uncategorized';
    const amount = Math.abs(Number(t.amount));

    const monthMap = monthlyData.get(month)!;
    monthMap.set(catId, (monthMap.get(catId) || 0) + amount);
    categoriesSet.add(catId);
  }

  // Determine which categories to include
  let categoriesToShow: CategoryInfo[];
  if (categoryId === null || categoryId === undefined) {
    // "All expenses/income" - show all categories that appear
    categoriesToShow = allCategories.filter(c => categoriesSet.has(c.id));
    
    // Add uncategorized if it appears
    if (categoriesSet.has('uncategorized')) {
      const hasUncategorized = categoriesToShow.some(c => c.id === 'uncategorized');
      if (!hasUncategorized) {
        categoriesToShow.push({
          id: 'uncategorized',
          name: 'Uncategorized',
          color: '#6B7280', // Gray
        });
      }
    }
  } else if (categoryId === UNCATEGORIZED_FILTER) {
    // Only uncategorized - show as single category
    categoriesToShow = [{
      id: 'uncategorized',
      name: 'Uncategorized',
      color: '#6B7280', // Gray
    }];
  } else {
    // Specific category selected
    const cat = allCategories.find(c => c.id === categoryId);
    categoriesToShow = cat ? [cat] : [];
  }

  // Assign colors to categories that don't have one
  categoriesToShow = categoriesToShow.map((cat, idx) => ({
    ...cat,
    color: cat.color || DEFAULT_CATEGORY_COLORS[idx % DEFAULT_CATEGORY_COLORS.length],
  }));

  // Build points array
  const points: MonthlySpendPoint[] = [];
  const totalByCategory = new Map<string, number>();

  for (let month = 0; month < 12; month++) {
    const point: MonthlySpendPoint = {
      monthIndex: month,
      label: MONTH_SHORT[month],
    };

    const monthMap = monthlyData.get(month)!;
    for (const cat of categoriesToShow) {
      const amount = monthMap.get(cat.id) || 0;
      point[cat.id] = amount;
      
      // Accumulate totals
      totalByCategory.set(cat.id, (totalByCategory.get(cat.id) || 0) + amount);
    }

    points.push(point);
  }

  return {
    year,
    points,
    categories: categoriesToShow,
    totalByCategory,
  };
}

// ---------------------------------------------------------------------------
// Yearly category totals for multi-year comparison table
// ---------------------------------------------------------------------------

export interface YearlyCategoryRow {
  categoryId: string;
  categoryName: string;
  categoryType: 'income' | 'expense' | 'transfer';
  color: string;
  /** Amount for two years ago (full year) */
  twoYearsAgo: number;
  /** Amount for previous year (full year) */
  previousYear: number;
  /** Amount for previous year (YTD aligned to current year day-of-year) */
  previousYearYtd: number;
  /** Amount for current year (YTD) */
  currentYearYtd: number;
}

export interface YearlyCategoryTotals {
  twoYearsAgoYear: number;
  previousYear: number;
  currentYear: number;
  rows: YearlyCategoryRow[];
  totals: {
    twoYearsAgo: number;
    previousYear: number;
    previousYearYtd: number;
    currentYearYtd: number;
  };
}

export interface ComputeYearlyCategoryTotalsOptions {
  /** Override "today" for testing */
  today?: Date;
  /** If set, include only this account */
  accountId?: string | null;
}

/**
 * Compute yearly totals by category for a multi-year comparison table.
 * Returns rows for all categories (both income and expense) with totals for:
 * - Two years ago (full year)
 * - Previous year (full year)
 * - Current year (YTD)
 */
export function computeYearlyCategoryTotals(
  transactions: TransactionWithRelations[],
  allCategories: { id: string; name: string; type: string; color: string | null; sortOrder: number }[],
  options: ComputeYearlyCategoryTotalsOptions = {}
): YearlyCategoryTotals {
  const rawToday = options.today ? new Date(options.today) : new Date();
  const today = toUTCNoon(rawToday);
  const currentYear = today.getUTCFullYear();
  const prevYear = currentYear - 1;
  const twoYearsAgoYear = currentYear - 2;

  // Get the current day of year for YTD calculation
  const startCurrent = startOfYearUTC(currentYear);
  const currentDayIndex = Math.max(0, differenceInDaysUTC(today, startCurrent));
  const startPrev = startOfYearUTC(prevYear);

  // Filter transactions by account if specified
  const filteredTransactions = options.accountId
    ? transactions.filter(t => t.accountId === options.accountId)
    : transactions;

  // Build a map of category totals by year
  // Key: `${categoryId}-${year}`, Value: total amount
  const categoryYearTotals = new Map<string, number>();

  // Separate map for previous year YTD (aligned to current year day-of-year)
  const categoryPrevYearYtdTotals = new Map<string, number>();

  for (const t of filteredTransactions) {
    // Skip transfer transactions
    if (t.category && t.category.type === 'transfer') continue;

    const parsed = parseBudgetDate(t.date);
    if (!parsed) continue;

    const txYear = parsed.getUTCFullYear();
    
    // Only include transactions from the three years we care about
    if (txYear !== currentYear && txYear !== prevYear && txYear !== twoYearsAgoYear) continue;

    // For current year, only include transactions up to today
    if (txYear === currentYear) {
      const txDayIndex = differenceInDaysUTC(parsed, startCurrent);
      if (txDayIndex > currentDayIndex) continue;
    }

    // For previous year YTD, also include transactions up to the aligned day-of-year.
    // Example: if today is Jan 11, 2026, include up to Jan 11, 2025.
    if (txYear === prevYear) {
      const txDayIndexPrev = differenceInDaysUTC(parsed, startPrev);
      if (txDayIndexPrev <= currentDayIndex) {
        const categoryId = t.categoryId || 'uncategorized';
        categoryPrevYearYtdTotals.set(
          categoryId,
          (categoryPrevYearYtdTotals.get(categoryId) || 0) + Number(t.amount)
        );
      }
    }

    const categoryId = t.categoryId || 'uncategorized';
    const key = `${categoryId}-${txYear}`;
    const amount = Number(t.amount);
    
    // For expenses (negative amounts or expense category with positive amount), use absolute value
    // For income (positive amounts with income category), use actual value
    // This ensures consistent sign handling
    categoryYearTotals.set(key, (categoryYearTotals.get(key) || 0) + amount);
  }

  // Build rows for all categories that have any transactions in any of the three years
  const categoriesWithData = new Set<string>();
  Array.from(categoryYearTotals.keys()).forEach(key => {
    // Use lastIndexOf to handle UUID category IDs that contain hyphens
    // Key format: `${categoryId}-${year}` where categoryId may be a UUID like "abc-def-123"
    const lastDashIndex = key.lastIndexOf('-');
    const categoryId = key.substring(0, lastDashIndex);
    categoriesWithData.add(categoryId);
  });

  // Sort categories: income first (by sortOrder), then expenses (by sortOrder)
  const sortedCategories = [...allCategories]
    .filter(c => c.type === 'income' || c.type === 'expense')
    .sort((a, b) => {
      // Income categories first
      if (a.type === 'income' && b.type !== 'income') return -1;
      if (a.type !== 'income' && b.type === 'income') return 1;
      // Within same type, sort by sortOrder
      return a.sortOrder - b.sortOrder;
    });

  const rows: YearlyCategoryRow[] = [];
  let totalTwoYearsAgo = 0;
  let totalPrevYear = 0;
  let totalPrevYearYtd = 0;
  let totalCurrentYtd = 0;

  for (const category of sortedCategories) {
    if (!categoriesWithData.has(category.id)) continue;

    const twoYearsAgoAmount = categoryYearTotals.get(`${category.id}-${twoYearsAgoYear}`) || 0;
    const prevYearAmount = categoryYearTotals.get(`${category.id}-${prevYear}`) || 0;
    const prevYearYtdAmount = categoryPrevYearYtdTotals.get(category.id) || 0;
    const currentYtdAmount = categoryYearTotals.get(`${category.id}-${currentYear}`) || 0;

    rows.push({
      categoryId: category.id,
      categoryName: category.name,
      categoryType: category.type as 'income' | 'expense' | 'transfer',
      color: category.color || '#6B7280',
      twoYearsAgo: twoYearsAgoAmount,
      previousYear: prevYearAmount,
      previousYearYtd: prevYearYtdAmount,
      currentYearYtd: currentYtdAmount,
    });

    totalTwoYearsAgo += twoYearsAgoAmount;
    totalPrevYear += prevYearAmount;
    totalPrevYearYtd += prevYearYtdAmount;
    totalCurrentYtd += currentYtdAmount;
  }

  // Add uncategorized row if there's any uncategorized data
  if (categoriesWithData.has('uncategorized')) {
    const twoYearsAgoAmount = categoryYearTotals.get(`uncategorized-${twoYearsAgoYear}`) || 0;
    const prevYearAmount = categoryYearTotals.get(`uncategorized-${prevYear}`) || 0;
    const prevYearYtdAmount = categoryPrevYearYtdTotals.get('uncategorized') || 0;
    const currentYtdAmount = categoryYearTotals.get(`uncategorized-${currentYear}`) || 0;

    rows.push({
      categoryId: 'uncategorized',
      categoryName: 'Uncategorized',
      categoryType: 'expense',
      color: '#6B7280',
      twoYearsAgo: twoYearsAgoAmount,
      previousYear: prevYearAmount,
      previousYearYtd: prevYearYtdAmount,
      currentYearYtd: currentYtdAmount,
    });

    totalTwoYearsAgo += twoYearsAgoAmount;
    totalPrevYear += prevYearAmount;
    totalPrevYearYtd += prevYearYtdAmount;
    totalCurrentYtd += currentYtdAmount;
  }

  return {
    twoYearsAgoYear,
    previousYear: prevYear,
    currentYear,
    rows,
    totals: {
      twoYearsAgo: totalTwoYearsAgo,
      previousYear: totalPrevYear,
      previousYearYtd: totalPrevYearYtd,
      currentYearYtd: totalCurrentYtd,
    },
  };
}
